import { useImportCalendarCsv } from '@/features/appointments/api/import-calendar-csv';
import { useImportProductsCsv } from '@/features/inventory/api/import-products-csv';
import { useImportLeadsCsv } from '@/features/leads/api/import-leads-csv';
import { useImportMembershipPlansCsv } from '@/features/memberships/api/import-membership-plans-csv';
import { useImportOffersCsv } from '@/features/offers/api/import-offers-csv';
import { useImportServicesCsv } from '@/features/organization-services/api/import-services-csv';
import { useImportResourcesCsv } from '@/features/resources/api/import-resources-csv';
import type { ImportKind } from '@borradh-workspace/api-client/types';
import { useDetectImportKind } from './detect-import-kind';
import type { ImportOptions, Importer, SpreadsheetUpload } from './importer';

const APP_IMPORT_KINDS = [
  'customers',
  'services',
  'products',
  'membershipPlans',
  'offers',
  'resources',
  'calendar',
] as const satisfies readonly ImportKind[];

/** The signed-in user's own org: each kind goes through its own endpoint. */
export function useAppImporter(): Importer {
  const { detectImportKind, isDetecting } = useDetectImportKind();
  const services = useImportServicesCsv();
  const customers = useImportLeadsCsv();
  const products = useImportProductsCsv();
  const plans = useImportMembershipPlansCsv();
  const offers = useImportOffersCsv();
  const resources = useImportResourcesCsv();
  const calendar = useImportCalendarCsv();

  // The six inputs are distinct types, so the dispatch stays a typed switch
  // rather than a table that would need casts.
  const run = (
    kind: ImportKind,
    upload: SpreadsheetUpload,
    options: ImportOptions
  ) => {
    const onDuplicate: 'update' | 'skip' = options.updateExisting
      ? 'update'
      : 'skip';
    switch (kind) {
      case 'customers':
        return customers.importCsvAsync({
          ...upload,
          deduplicateBy: 'email',
          onDuplicate,
          markAsClients: options.customersAreClients ?? true,
          consentAcknowledgment: options.consentAcknowledgment,
          marketingAuthorized: options.marketingAuthorized ?? false,
          defaultConsentEmail: options.defaultConsentEmail,
          defaultConsentWhatsapp: options.defaultConsentWhatsapp,
          defaultConsentSms: options.defaultConsentSms,
          defaultConsentVoice: options.defaultConsentVoice,
        });
      case 'services':
        return services.importServicesCsvAsync({
          ...upload,
          onDuplicate,
          createMissingCategories: options.syncCategories ?? true,
          applyCategoriesToExisting: options.syncCategories ?? true,
          importAsInactive: options.importAsInactive,
        });
      case 'products':
        return products.importProductsCsvAsync({
          ...upload,
          onDuplicate,
          createMissingCategories: true,
          createMissingBrands: true,
          importAsNonRetail: options.importAsNonRetail,
        });
      case 'membershipPlans':
        return plans.importMembershipPlansCsvAsync({
          ...upload,
          onDuplicate,
          importAsInactive: options.importAsInactive,
        });
      case 'offers':
        return offers.importOffersCsvAsync({
          ...upload,
          onDuplicate,
          importAsDraft: options.importAsDraft,
        });
      case 'resources':
        return resources.importResourcesCsvAsync({
          ...upload,
          onDuplicate,
          importAsInactive: options.importAsInactive,
        });
      case 'calendar':
        return calendar.importCalendarCsvAsync({
          ...upload,
          createMissingCustomers: options.createMissingCustomers,
          consentAcknowledgment: options.consentAcknowledgment,
          onDuplicate,
          rowSelection: 'auto',
        });
      case 'teamMembers':
      case 'shifts':
        return Promise.reject(
          new Error(
            'Team member and shift imports are available in Admin Imports'
          )
        );
    }
  };

  return {
    supportedKinds: APP_IMPORT_KINDS,
    detect: detectImportKind,
    isDetecting,
    isImporting: [
      customers,
      services,
      products,
      plans,
      offers,
      resources,
      calendar,
    ].some((hook) => hook.isImporting),
    run,
  };
}
